import React, { useCallback, useRef, useState } from "react";
import { api, apiFetch } from "../services/api";
import {
  beginOptimisticEnrichmentQueue,
  finalizeOptimisticEnrichmentQueue,
  finalizeOptimisticEnrichmentQueueOnTimeout,
  isEnrichmentQueueTimeoutError,
  rollbackOptimisticEnrichmentQueue,
} from "../utils/enrichmentClientCache";
import { normalizeEntityType } from "./relationships/SectionConfig";
import { isEnrichmentAnchorType } from "../config/enrichmentAnchorTypes";
import { createDevConsole } from "../utils/devLogger";
import {
  isLlmQuotaExhausted,
  LLM_QUOTA_EXHAUSTED_MESSAGE,
} from "../utils/llmQuota";

const console = createDevConsole();

/**
 * Enrich from pasted text or uploaded file (allowlisted architecture entities).
 * Queues application_enrichment jobs; review in Insights Inbox.
 */
const EntityEnrichPanel = ({
  anchorEntityId,
  anchorName,
  anchorEntityType,
  readOnly,
  onEnrichmentQueued = null,
  variant = "card",
  /** "dark" matches EntityForm modals; "light" matches v2 explorer surfaces. */
  tone = "dark",
  /** Plain-language copy for v2 map surfaces. */
  mapLanguage = false,
}) => {
  const [text, setText] = useState("");
  const [pendingFileText, setPendingFileText] = useState("");
  const [pendingFileName, setPendingFileName] = useState("");
  const [pendingFileParserVersion, setPendingFileParserVersion] = useState("");
  const [pendingFileType, setPendingFileType] = useState("");
  const [pendingFileBlobId, setPendingFileBlobId] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ kind: "", text: "" });
  const fileInputRef = useRef(null);

  const normalizedType = normalizeEntityType(anchorEntityType);
  const isInitiative =
    normalizedType === "initiative" || normalizedType === "initiatives";
  const isEnrichable = isEnrichmentAnchorType(normalizedType);

  const clearMessage = () => setMessage({ kind: "", text: "" });

  const ensureTextQuota = useCallback(async () => {
    if (isLlmQuotaExhausted()) {
      setMessage({ kind: "error", text: LLM_QUOTA_EXHAUSTED_MESSAGE });
      return false;
    }
    return true;
  }, []);

  const trackTextUsage = useCallback(async () => {}, []);

  const buildSources = useCallback(() => {
    const sources = [];
    const pasted = String(text || "").trim();
    const fileBody = String(pendingFileText || "").trim();
    if (pasted) {
      sources.push({
        kind: "snippet",
        label: "Pasted text",
        text: pasted,
      });
    }
    if (fileBody) {
      const docSource = {
        kind: "document",
        label: pendingFileName || "Uploaded document",
        text: fileBody,
      };
      if (pendingFileParserVersion) {
        docSource.parser_version = pendingFileParserVersion;
      }
      if (pendingFileType) {
        docSource.file_type = pendingFileType;
      }
      if (pendingFileBlobId) {
        docSource.file_blob_id = pendingFileBlobId;
        docSource.original_filename = pendingFileName || undefined;
      }
      sources.push(docSource);
    }
    return sources;
  }, [
    text,
    pendingFileText,
    pendingFileName,
    pendingFileParserVersion,
    pendingFileType,
    pendingFileBlobId,
  ]);

  const submitEnrichment = useCallback(async () => {
    const sources = buildSources();
    if (sources.length === 0) {
      setMessage({
        kind: "error",
        text: "Enter text and/or upload a file, then click Submit.",
      });
      return;
    }
    const id = String(anchorEntityId || "").trim();
    if (!id) {
      setMessage({
        kind: "error",
        text: mapLanguage
          ? "Save this item before adding knowledge."
          : "Save the entity before running enrichment.",
      });
      return;
    }
    if (!isEnrichable) {
      setMessage({
        kind: "error",
        text: mapLanguage
          ? "Adding knowledge from here works for architecture entities on your map (applications, organizations, initiatives, capabilities, and more). Open one of those, or use Home → Add knowledge."
          : "AI enrichment with Insights Inbox review is available for architecture entities. Open one in Explorer, or use Home → Add knowledge.",
      });
      return;
    }

    clearMessage();
    const okQuota = await ensureTextQuota();
    if (!okQuota) {
      return;
    }

    setBusy(true);
    let provisionalId = null;
    try {
      provisionalId = beginOptimisticEnrichmentQueue({
        anchorName: anchorName || "Application",
        anchorApplicationId: id,
      });

      await trackTextUsage();

      const data = await api.queueApplicationEnrichmentJob(id, sources);
      const jobId = finalizeOptimisticEnrichmentQueue(provisionalId, data, {
        anchorName: anchorName || "Application",
        anchorApplicationId: id,
      });
      setMessage({
        kind: "success",
        text: mapLanguage
          ? "Your upload is queued. Processing can take a few minutes — check Inbox when results are ready."
          : "Enrichment job queued successfully. Processing can take a few minutes. You can continue with other work, and review results in Insights Inbox when ready.",
      });
      setText("");
      setPendingFileText("");
      setPendingFileName("");
      setPendingFileParserVersion("");
      setPendingFileType("");
      setPendingFileBlobId("");
      if (jobId && typeof onEnrichmentQueued === "function") {
        onEnrichmentQueued(jobId);
      }
    } catch (e) {
      if (isEnrichmentQueueTimeoutError(e)) {
        finalizeOptimisticEnrichmentQueueOnTimeout(provisionalId, {
          anchorName: anchorName || "Application",
          anchorApplicationId: id,
        });
        setMessage({
          kind: "success",
          text: mapLanguage
            ? "Your request may still be queued. Check Inbox for the job."
            : "Your enrichment request may still be queued. Check Insights Inbox for the job.",
        });
        return;
      }
      if (provisionalId) {
        rollbackOptimisticEnrichmentQueue(provisionalId);
      }
      setMessage({
        kind: "error",
        text: e?.message || String(e) || "Enrichment failed.",
      });
    } finally {
      setBusy(false);
    }
  }, [
    anchorEntityId,
    anchorName,
    buildSources,
    ensureTextQuota,
    isEnrichable,
    mapLanguage,
    onEnrichmentQueued,
    trackTextUsage,
  ]);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (!file) {
      return;
    }
    clearMessage();
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiFetch("/api/upload-file", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        let detail = "File upload failed";
        try {
          const err = await response.json();
          detail = err.detail || detail;
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      const result = await response.json();
      const content = result.extracted_text || "";
      if (!String(content).trim()) {
        setMessage({
          kind: "error",
          text: "No text could be extracted from that file.",
        });
        return;
      }
      setPendingFileText(content);
      setPendingFileName(file.name || "Uploaded file");
      setPendingFileParserVersion(result.file_info?.parser_version || "");
      setPendingFileType(result.file_info?.file_type || "");
      setPendingFileBlobId(result.file_info?.file_blob_id || "");
      setMessage({
        kind: "success",
        text: `File loaded (${file.name}). Click Submit to queue enrichment.`,
      });
    } catch (err) {
      setMessage({
        kind: "error",
        text: err?.message || String(err) || "File processing failed.",
      });
    } finally {
      setBusy(false);
    }
  };

  if (readOnly || !anchorEntityId) {
    return null;
  }

  const isDialog = variant === "dialog";
  const isLight = tone === "light";

  const cardShell = isLight
    ? "mt-8 mb-6 border border-slate-200 rounded-xl bg-white shadow-sm p-4"
    : "mt-8 mb-6 border border-gray-700 rounded-lg bg-gray-800/60 p-4";

  const introText = isLight ? "text-sm text-slate-600" : "text-sm text-gray-400";
  const anchorHighlight = isLight ? "text-slate-900 font-medium" : "text-gray-200 font-medium";
  const labelUpper = isLight
    ? "text-xs font-medium uppercase tracking-wide text-slate-500 mb-2"
    : "text-xs font-medium uppercase tracking-wide text-gray-400 mb-2";
  const hintSmall = isLight ? "text-xs text-slate-500 mb-2" : "text-xs text-gray-500 mb-2";
  const textareaCls = isLight
    ? "w-full rounded-lg border border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 text-sm p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
    : "w-full rounded-md border border-gray-600 bg-gray-900 text-gray-100 text-sm p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50";
  const fileStagedCls = isLight ? "text-xs text-slate-600" : "text-xs text-gray-400";
  const fileStagedName = isLight ? "text-slate-900" : "text-gray-200";
  const primaryBtn = isLight
    ? "px-4 py-2 bg-blue-700 hover:bg-blue-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
    : "px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md transition-colors";
  const secondaryBtn = isLight
    ? "px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-slate-800 transition-colors"
    : "px-4 py-2 rounded-md text-sm font-medium border border-gray-600 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors";
  const footerHint = isLight ? "text-xs text-slate-500" : "text-xs text-gray-500";
  const msgErr = isLight ? "text-rose-600" : "text-red-300";
  const msgOk = isLight ? "text-emerald-700" : "text-green-300";

  return (
    <div className={isDialog ? "space-y-4" : cardShell}>
      {!isDialog ? (
        <h2
          className={
            isLight
              ? "text-lg font-semibold text-slate-900 mb-1"
              : "text-lg font-semibold text-white mb-1"
          }
        >
          {isEnrichable ? "Enrich from AI" : "Enrich from text or file"}
        </h2>
      ) : null}
      <p className={`${introText} ${isDialog ? "" : "mb-3"}`}>
        {isEnrichable ? (
          <>
            {mapLanguage ? (
              <>
                Paste notes or upload a document, then click <strong>Submit</strong>. Processing
                runs in the background — you can close this and keep working. When results are
                ready, open <strong>Inbox</strong> in the sidebar to review them.
              </>
            ) : (
              <>
                Paste notes or upload a document, then click <strong>Submit</strong> to queue
                analysis. Processing runs in the background — you can close this dialog and keep
                working. When results are ready, a bell appears on <strong>Insights</strong> in the
                sidebar; open <strong>Insights Inbox</strong> there to review them (same workflow as
                today).
              </>
            )}
          </>
        ) : (
          <>
            Paste notes or upload a document to discover related items and connect them to{" "}
            <span className={anchorHighlight}>{anchorName || "this record"}</span>.
            {isInitiative ? (
              <>
                {" "}
                For initiatives, applications, interfaces, and data in scope often link to
                this initiative.
              </>
            ) : null}
          </>
        )}
      </p>

      <div className="space-y-3">
        <div>
          <p className={labelUpper}>Paste notes, requirements, workshop output...</p>
          <p className={hintSmall}>Optional when a file is uploaded. You can provide both.</p>
        </div>
        <textarea
          value={text}
          onChange={(ev) => {
            clearMessage();
            setText(ev.target.value);
          }}
          disabled={busy}
          rows={5}
          placeholder="Paste notes, requirements, workshop output…"
          className={textareaCls}
        />
        {pendingFileName ? (
          <p className={fileStagedCls}>
            File staged: <span className={fileStagedName}>{pendingFileName}</span> (
            {pendingFileText.length.toLocaleString()} chars) — will be included when you
            submit.
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={busy || (!text.trim() && !pendingFileText.trim())}
            onClick={() => void submitEnrichment()}
            className={primaryBtn}
          >
            {busy
              ? "Working…"
              : isEnrichable
                ? mapLanguage
                  ? "Submit"
                  : "Submit for enrichment"
                : mapLanguage
                  ? "Queue analysis"
                  : "Queue enrichment"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            disabled={busy}
            accept=".txt,.md,.pdf,.doc,.docx,.pptx,.html,.htm,.csv,.xlsx,.xls"
            onChange={(ev) => void handleFile(ev)}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            className={secondaryBtn}
          >
            Upload file…
          </button>
          <span className={footerHint}>
            {mapLanguage
              ? "Upload only prepares text; analysis starts when you submit."
              : "Upload only prepares text; enrichment starts when you submit."}
          </span>
        </div>
      </div>

      {message.text ? (
        <p className={`mt-3 text-sm ${message.kind === "error" ? msgErr : msgOk}`}>
          {message.text}
        </p>
      ) : null}
    </div>
  );
};

export default EntityEnrichPanel;
